import { useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Building2,
  ChevronDown,
  Maximize2,
  PanelRightClose,
  SquarePen,
  User,
  Users,
} from "lucide-react";

import { cn } from "@/lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { AnserMark } from "@/components/ui/anser-mark";
import { useProfile } from "@/context/ProfileContext";
import { useTeamScope } from "@/context/TeamScopeContext";
import { useCoachRail } from "@/context/CoachRailContext";
import { useAnserPanelOverride } from "@/context/AnserPanelContext";
import { useIntelligenceAccess } from "@/hooks/use-intelligence-access";
import { useCanDockInspector } from "@/hooks/use-inspector-dock";
import { useIsMobile } from "@/hooks/use-mobile";
import { useVisibleSidebarNavFeatures } from "@/hooks/use-sidebar-nav-features";
import { useIntelligenceTopics } from "@/hooks/queries/use-intelligence-topics";
import {
  useSalesTeams,
  useSalesTeamsMembers,
} from "@/hooks/queries/use-sales-teams";
import { useSalesInsightThread } from "@/hooks/queries/use-sales-insight-threads";
import { useIntelligenceScope } from "@/hooks/queries/use-intelligence-scope";
import { IntelligenceChat } from "@/components/intelligence/IntelligenceChat";
import { ScopePicker } from "@/components/intelligence/ScopePicker";
import {
  parseScopeParam,
  scopeToParam,
  type ChatScope,
} from "@/components/intelligence/scope";
import {
  deriveChatScope,
  deriveNonOrgDefaultScope,
  deriveTopicKey,
} from "@/components/intelligence/chat-scope-derivation";
import { getLocalizedEffectiveTopics } from "@/components/intelligence/topics";
import { defaultAnserContext } from "@/lib/anser-context";
import { CoachThreadPicker } from "./CoachThreadPicker";
import { TodayPanelBody, TodayPanelHeader } from "./TodayPanel";
import { useAnserTips } from "./anser-tips";
import { isRailSuppressed } from "./rail-visibility";

const iconButtonClassName = cn(
  "inline-flex size-8 items-center justify-center rounded-g-sm text-g-ink-400 transition-colors duration-150",
  "hover:bg-g-primary-wash hover:text-g-ink-900",
  "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-g-ring",
);

/**
 * The Anser panel (GRA-149): `IntelligenceChat` lifted into a persistent
 * layout slot so its thread survives navigating Översikt → Medarbetare →
 * Mål. Reads cross-route state from {@link useCoachRail} and the current
 * page's context from {@link useAnserPanelOverride}. Docked as a flush
 * column on wide viewports (nothing renders when closed; the header toggle
 * reopens it) and as a right overlay sheet below the dock breakpoint.
 */
export function CoachRail() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation("intelligence");
  const { t: tNav } = useTranslation("nav");
  const { businessProfile } = useProfile();
  const businessId = businessProfile?.uid ?? "";
  const entitled = useIntelligenceAccess();
  const canDock = useCanDockInspector();
  const isMobile = useIsMobile();
  const features = useVisibleSidebarNavFeatures();
  const override = useAnserPanelOverride();

  const {
    open,
    openRail,
    closeRail,
    activeThreadId,
    selectThread,
    newThread,
    resetNonce,
    threadPickerOpen,
    setThreadPickerOpen,
    scopeParam,
    setScopeParam,
    topicParam,
    setTopicParam,
    pendingMessage,
    clearPendingMessage,
  } = useCoachRail();

  const suppressed = isRailSuppressed(location.pathname);

  // ── Data ──────────────────────────────────────────────────────────────────
  const topicsQuery = useIntelligenceTopics(businessId);
  const topics = getLocalizedEffectiveTopics(t, topicsQuery.data?.topics);

  const teamsQuery = useSalesTeams(businessId);
  const membersQuery = useSalesTeamsMembers(businessId);
  const scopeAccessQuery = useIntelligenceScope(businessId);
  const restrictToSubtree = scopeAccessQuery.data?.mode === "scoped";
  const { activeTeamId } = useTeamScope();

  const threadQuery = useSalesInsightThread(businessId, activeThreadId);

  const membersById = useMemo(() => {
    const m = new Map<string, { salesTeamId: string | null }>();
    for (const mem of membersQuery.data ?? [])
      m.set(mem.id, { salesTeamId: mem.salesTeamId });
    return m;
  }, [membersQuery.data]);

  const activeTeamScope = useMemo((): ChatScope | null => {
    if (!activeTeamId) return null;
    const teams = teamsQuery.data ?? [];
    return teams.some((tm) => tm.id === activeTeamId)
      ? { kind: "team", salesTeamId: activeTeamId }
      : null;
  }, [activeTeamId, teamsQuery.data]);

  const parsedScope = useMemo(
    () => parseScopeParam(scopeParam, membersById),
    [scopeParam, membersById],
  );

  const nonOrgDefaultScope = useMemo(
    () =>
      deriveNonOrgDefaultScope({
        activeTeamScope,
        teamsLoading: teamsQuery.isLoading,
        teams: teamsQuery.data ?? [],
        membersLoading: membersQuery.isLoading,
        members: membersQuery.data ?? [],
      }),
    [
      activeTeamScope,
      teamsQuery.isLoading,
      teamsQuery.data,
      membersQuery.isLoading,
      membersQuery.data,
    ],
  );

  const scope = useMemo(
    () =>
      deriveChatScope({
        threadId: activeThreadId,
        threadData: threadQuery.data,
        membersById,
        parsedScope,
        nonOrgDefaultScope,
        restrictToSubtree,
        scopeParam,
        activeTeamScope,
      }),
    [
      activeThreadId,
      threadQuery.data,
      membersById,
      parsedScope,
      nonOrgDefaultScope,
      restrictToSubtree,
      scopeParam,
      activeTeamScope,
    ],
  );

  const topicKey = useMemo(
    () =>
      deriveTopicKey({
        threadData: threadQuery.data,
        topicParam,
        topics,
      }),
    [threadQuery.data, topicParam, topics],
  );

  // Drop a stale/deleted active thread so the rail falls back to a fresh thread.
  useEffect(() => {
    if (!activeThreadId || threadQuery.isLoading) return;
    if (!threadQuery.data) selectThread(null);
  }, [activeThreadId, threadQuery.isLoading, threadQuery.data, selectThread]);

  // ── Context ───────────────────────────────────────────────────────────────
  // The header line and intro come from the page (override) or the route.
  // The scope chip stays because it is the only context the chat API
  // actually receives; the page context is for the reader, not the model.
  const routeContext = useMemo(
    () => defaultAnserContext(location.pathname, features, tNav),
    [location.pathname, features, tNav],
  );
  const contextLabel = override?.label ?? routeContext.label;
  const intro = tNav(`anser.intro.${override?.introKey ?? routeContext.introKey}`);

  const scopeLabel = useMemo(() => {
    if (scope.kind === "business") return t("scope.organization");
    if (scope.kind === "team") {
      const team = (teamsQuery.data ?? []).find(
        (tm) => tm.id === scope.salesTeamId,
      );
      return team?.name ?? t("scope.team");
    }
    const member = (membersQuery.data ?? []).find(
      (m) => m.id === scope.memberId,
    );
    return member?.name ?? t("scope.member");
  }, [scope, teamsQuery.data, membersQuery.data, t]);

  const memberIdForTips = scope.kind === "member" ? scope.memberId : null;
  const dataTips = useAnserTips(businessId, entitled && open, memberIdForTips);
  const tips = override?.tips ?? dataTips;
  const quickQuestions = useMemo(
    () => override?.quickQuestions ?? tips.slice(0, 2).flatMap((tip) => (tip.prompt ? [tip.prompt] : [])),
    [override?.quickQuestions, tips],
  );

  const threadLoading = Boolean(activeThreadId) && threadQuery.isLoading;
  const threadTitle = !activeThreadId
    ? t("threads.new")
    : threadLoading
      ? t("threads.loading")
      : threadQuery.data?.title?.trim() || t("threads.untitled");

  const scopeLocked = Boolean(activeThreadId);
  const ScopeIcon =
    scope.kind === "member" ? User : scope.kind === "team" ? Users : Building2;

  const handleMaximize = () => {
    const sp = new URLSearchParams();
    if (activeThreadId) sp.set("thread", activeThreadId);
    else sp.set("scope", scopeToParam(scope));
    navigate(`/coach?${sp.toString()}`);
  };

  // The home page hands over its Idag data; the panel shows it instead of
  // the tips until a thread starts, then it is a chat like everywhere else.
  const today = !activeThreadId ? (override?.today ?? null) : null;

  // ── Chrome ──────────────────────────────────────────────────────────────────
  const header = today ? (
    <TodayPanelHeader dateLabel={today.dateLabel} onClose={closeRail} />
  ) : (
    <div className="flex shrink-0 flex-col gap-3 px-[22px] pt-6">
      <div className="flex items-center gap-3">
        <AnserMark size={46} glow />
        <div className="min-w-0 flex-1">
          <div className="text-[16px] font-bold leading-tight text-g-ink-900">
            {t("rail.title")}
          </div>
          <div
            className="truncate text-[12px] text-g-ink-400"
            data-testid="anser-context"
          >
            {contextLabel}
          </div>
        </div>
        <button
          type="button"
          className={iconButtonClassName}
          onClick={closeRail}
          aria-label={t("rail.close")}
          title={t("rail.close")}
        >
          <PanelRightClose className="size-4" />
        </button>
      </div>
      <div className="flex items-center gap-1">
        <Popover open={threadPickerOpen} onOpenChange={setThreadPickerOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="flex min-w-0 flex-1 items-center gap-1 rounded-g-sm px-1.5 py-1 text-left transition-colors hover:bg-g-primary-wash"
            >
              <span
                className={cn(
                  "truncate text-[13.5px] font-semibold",
                  threadLoading ? "text-g-ink-400" : "text-g-ink-900",
                )}
              >
                {threadTitle}
              </span>
              <ChevronDown className="size-4 shrink-0 text-g-ink-400" />
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" sideOffset={6} className="w-[300px] p-0">
            <CoachThreadPicker
              businessId={businessId}
              activeThreadId={activeThreadId}
              onSelect={selectThread}
              onNewChat={newThread}
            />
          </PopoverContent>
        </Popover>
        <button
          type="button"
          className={iconButtonClassName}
          onClick={newThread}
          aria-label={t("threads.new")}
          title={t("threads.new")}
        >
          <SquarePen className="size-4" />
        </button>
        <button
          type="button"
          className={iconButtonClassName}
          onClick={handleMaximize}
          aria-label={t("rail.maximize")}
          title={t("rail.maximize")}
        >
          <Maximize2 className="size-4" />
        </button>
      </div>
      <p className="text-[14px] leading-[1.55] text-g-ink-700">{intro}</p>
    </div>
  );

  const contextStrip = (
    <div className="flex shrink-0 flex-wrap items-center gap-1.5 px-[22px] py-2">
      <span className="text-[10.5px] font-semibold uppercase tracking-[0.06em] text-g-ink-400">
        {t("rail.context.label")}
      </span>
      {scopeLocked ? (
        <span
          title={t("scope.locked")}
          className="inline-flex items-center gap-1.5 rounded-g-pill border border-g-line bg-g-bg-card px-2.5 py-1 text-xs font-medium text-g-ink-500"
        >
          <ScopeIcon className="size-3" aria-hidden="true" />
          <span className="max-w-[160px] truncate">{scopeLabel}</span>
        </span>
      ) : (
        <ScopePicker
          businessId={businessId}
          scope={scope}
          onChange={(next) => setScopeParam(scopeToParam(next))}
          trigger="chip"
          hideOrganizationOption={restrictToSubtree}
        />
      )}
    </div>
  );

  const body = (
    <div className="min-h-0 flex-1">
      <IntelligenceChat
        key={`rail-${resetNonce}`}
        variant="rail"
        businessId={businessId}
        scope={scope}
        onScopeChange={(next) => setScopeParam(scopeToParam(next))}
        topicKey={topicKey}
        onTopicChange={(next) => setTopicParam(next)}
        threadId={activeThreadId}
        onThreadCreated={selectThread}
        onThreadDeleted={() => selectThread(null)}
        onNewChat={newThread}
        topics={topics}
        hideOrganizationScopeOption={restrictToSubtree}
        initialMessage={pendingMessage?.text ?? null}
        onInitialMessageConsumed={clearPendingMessage}
        railTips={tips}
        railQuickQuestions={quickQuestions}
        railEmptyState={
          today
            ? (prefill) => <TodayPanelBody data={today} onPick={prefill} />
            : undefined
        }
      />
    </div>
  );

  const content = (
    <div className="flex h-full min-h-0 flex-col bg-g-bg-shell font-g text-g-ink-900">
      {header}
      {today ? <div className="h-3 shrink-0" /> : contextStrip}
      {body}
    </div>
  );

  if (!entitled || suppressed) return null;

  // On phones the panel is a bottom sheet (M2) — a grab handle over the same
  // content; between the phone and dock breakpoints it's a right overlay sheet.
  if (!canDock) {
    return (
      <Sheet open={open} onOpenChange={(o) => (o ? openRail() : closeRail())}>
        {isMobile ? (
          <SheetContent
            side="bottom"
            className="h-[748px] max-h-[92vh] rounded-t-2xl p-0"
          >
            <SheetTitle className="sr-only">{t("rail.title")}</SheetTitle>
            <div className="flex h-full min-h-0 flex-col">
              <div className="flex shrink-0 justify-center py-2">
                <span className="h-1 w-9 rounded-g-pill bg-g-line-strong" />
              </div>
              <div className="min-h-0 flex-1">{content}</div>
            </div>
          </SheetContent>
        ) : (
          <SheetContent side="right" className="w-[360px] max-w-[92vw] p-0">
            <SheetTitle className="sr-only">{t("rail.title")}</SheetTitle>
            {content}
          </SheetContent>
        )}
      </Sheet>
    );
  }

  // Docked: a flush column beside the content, as in the sketch. Closed, the
  // panel renders nothing; the header's Anser toggle brings it back.
  if (!open) return null;

  return (
    <aside
      role="complementary"
      aria-label={t("rail.title")}
      className="flex h-full w-[clamp(280px,26vw,340px)] shrink-0 flex-col overflow-hidden border-l border-g-line bg-g-bg-shell"
    >
      {content}
    </aside>
  );
}
